// Import design tokens first - must be loaded before other styles
import './styles/design-tokens.css';

import React, { Suspense, lazy } from 'react';
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CookieNotice } from "@/components/layout/cookie-notice";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { useBusinessContext } from "./context/BusinessContext";
import { useSubscription } from "./context/SubscriptionContext";
import { canOpenPortfolio } from "./lib/constants/dashboard";
import { QueryClient, QueryClientProvider, focusManager } from "@tanstack/react-query";
import { AuthProvider } from "./context/AuthContext";
import { AppLockProvider } from "./context/AppLockContext";
import { ThemeProvider } from "./context/ThemeContext";
import { AccountSettingsProvider } from "./context/AccountSettingsContext";
import { BusinessSettingsProvider } from "./context/BusinessSettingsContext";
import { DashboardTabProvider } from "./context/DashboardTabContext";
import { ShowUserProvider } from "./context/ShowUserContext";
import { HighlightOverlay } from "./components/onboarding/highlight-overlay";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { EmployeeProvider } from "./context/EmployeeContext";
import { EmployeeSelectionProvider } from "./context/EmployeeSelectionContext";
import { PayrollHistoryProvider } from "./context/PayrollHistoryContext";
import { HistoricalEditProvider } from "./context/HistoricalEditContext";
import { BusinessProvider } from "./context/BusinessContext";
import { OnboardingProvider } from "./context/OnboardingContext";
import { AppTour } from "./components/onboarding/AppTour";
import { NotificationProvider } from "./context/NotificationContext";
import { UnifiedLoadingProvider } from "./context/UnifiedLoadingContext";
import { KeyboardShortcutsProvider } from "./context/KeyboardShortcutsContext";
import { CommandPaletteProvider } from "./context/CommandPaletteContext";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
const AuthPage = lazy(() => import("./pages/AuthPage"));
import EmployeeProtectedRoute from "./components/auth/EmployeeProtectedRoute";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import SubscriptionRequiredRoute from "./components/auth/SubscriptionRequiredRoute";
import AdminProtectedRoute from "./components/auth/AdminProtectedRoute";
import AutoLogout from "./components/auth/AutoLogout";
import AppLockScreen from "./components/auth/AppLockScreen";
import AuthErrorBoundary from "./components/auth/AuthErrorBoundary";
import MFAGate from "./components/auth/MFAGate";
import { GlobalLayout } from "./components/layout/GlobalLayout";
import AdminLayout from "./components/admin/AdminLayout";

// --- Lazy-loaded route pages ---
const Dashboard = lazy(() => import("./pages/Dashboard"));
const SubmitReview = lazy(() => import("./pages/SubmitReview"));
const ConfirmEmailPage = lazy(() => import("./pages/ConfirmEmailPage"));
const CreateBusiness = lazy(() => import("./pages/CreateBusiness"));
const BusinessDetails = lazy(() => import("./pages/BusinessDetails"));
const EditBusiness = lazy(() => import("./pages/EditBusiness"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const EmployeeDashboard = lazy(() => import("./pages/EmployeeDashboard"));
const EmployeePortalPage = lazy(() => import("./pages/EmployeePortalPage"));
const FranchiseDashboard = lazy(() => import("./pages/FranchiseDashboard"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const Changelog = lazy(() => import("./pages/Changelog"));
const PassBuilderPage = lazy(() => import("./pages/PassBuilderPage"));
const EnhancedGetPass = lazy(() => import("./pages/EnhancedGetPass"));
const UpdatePassPage = lazy(() => import("./pages/UpdatePassPage"));
const KioskPage = lazy(() => import("./pages/KioskPage"));
const GetPass = lazy(() => import("./pages/GetPass"));
const JobApplication = lazy(() => import("./pages/JobApplication"));
const JobDetails = lazy(() => import("./pages/JobDetails"));
const PublicReviewForm = lazy(() => import("./pages/PublicReviewForm"));
const PreviewReviewForm = lazy(() => import("./pages/PreviewReviewForm"));
const CouponTokenRedemption = lazy(() => import("./pages/CouponTokenRedemption"));
const SignagePlayer = lazy(() => import("./pages/signage/Player"));
const AccountSettingsPage = lazy(() => import("./pages/AccountSettingsPage"));
const BusinessSettingsPage = lazy(() => import("./pages/BusinessSettingsPage"));
const Pricing = lazy(() => import("./pages/Pricing"));
const SelectPlan = lazy(() => import("./pages/SelectPlan"));
const ManagePlan = lazy(() => import("./pages/ManagePlan"));
const Privacy = lazy(() => import("./pages/Privacy"));
const BusinessPrivacy = lazy(() => import("./pages/BusinessPrivacy"));
const Terms = lazy(() => import("./pages/Terms"));
const PublicBookingPage = lazy(() => import("./pages/public/PublicBookingPage"));
const UnsubscribeSuccess = lazy(() => import("./components/public/UnsubscribeSuccess"));
const UnsubscribeAlready = lazy(() => import("./components/public/UnsubscribeAlready"));
const UnsubscribeError = lazy(() => import("./components/public/UnsubscribeError"));

// --- Lazy admin pages ---
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const WaitlistManagement = lazy(() => import("./pages/admin/WaitlistManagement"));
const UsersManagement = lazy(() => import("./pages/admin/UsersManagement"));
const BusinessesOverview = lazy(() => import("./pages/admin/BusinessesOverview"));
const EmailTemplatesManagement = lazy(() => import("./pages/admin/EmailTemplatesManagement"));


// Completely disable TanStack Query's focus manager to prevent re-renders on tab focus
focusManager.setEventListener(() => {
  return () => {};
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    },
  },
});

const HomeRoute = () => {
  const { user, loading, isEmployee, isEmployeeLoading, employeeAccessLevel } = useAuth();
  const { businesses, loadingBusinesses } = useBusinessContext();
  const { currentPlan, isLoading: planLoading } = useSubscription();

  if (loading || isEmployeeLoading || (user && !isEmployee && (loadingBusinesses || planLoading))) {
    return <Index />;
  }

  if (user) {
    if (isEmployee) {
      return employeeAccessLevel === 'admin'
        ? <Navigate to="/dashboard" replace />
        : <Navigate to="/employee-portal" replace />;
    }
    if (businesses.length === 0) return <Navigate to="/create-business" replace />;
    if (canOpenPortfolio(currentPlan?.name, businesses.length)) return <Navigate to="/portfolio" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return <Index />;
};

const RouteFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
      <AuthErrorBoundary>
        <AuthProvider>
          <MFAGate>
          <AppLockProvider>
          <EmployeeProvider>
            <EmployeeSelectionProvider>
              <SubscriptionProvider>
                <PayrollHistoryProvider>
                  <ThemeProvider>
                    <UnifiedLoadingProvider>
                    <Sonner />
                          <ShowUserProvider>
                          <HistoricalEditProvider>
                            <TooltipProvider>
                            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                              <HighlightOverlay />
                              <CookieNotice />
                              <AutoLogout />
                              <AppLockScreen />
                              <DashboardTabProvider>
                              <BusinessProvider>
                              <AccountSettingsProvider>
                              <BusinessSettingsProvider>
                              <OnboardingProvider>
                              <AppTour />
                              <NotificationProvider>
                              <KeyboardShortcutsProvider>
                              <CommandPaletteProvider>
                              <GlobalLayout>
                              <Suspense fallback={<RouteFallback />}>
                              <Routes>
                                <Route path="/" element={<HomeRoute />} />
                                <Route path="/dashboard" element={<SubscriptionRequiredRoute><Dashboard /></SubscriptionRequiredRoute>} />
                                <Route path="/submit-review" element={<SubmitReview />} />
                                <Route path="/auth" element={<AuthPage />} />
                                <Route path="/confirm-email" element={<ConfirmEmailPage />} />
                                <Route path="/sign-in" element={<Navigate to="/auth" replace />} />
                                <Route path="/sign-up" element={<Navigate to="/auth?mode=sign-up" replace />} />
                                <Route path="/signup" element={<Navigate to="/auth?mode=sign-up" replace />} />
                                <Route path="/waitlist" element={<Navigate to="/auth?mode=sign-up" replace />} />
                                <Route path="/select-plan" element={<ProtectedRoute><SelectPlan /></ProtectedRoute>} />
                                <Route path="/manage-plan" element={<ProtectedRoute><ManagePlan /></ProtectedRoute>} />
                                <Route path="/create-business" element={<SubscriptionRequiredRoute><CreateBusiness /></SubscriptionRequiredRoute>} />
                                <Route path="/business/:businessId" element={<BusinessDetails />} />
                                <Route path="/edit-business/:businessId" element={<EditBusiness />} />
                                <Route path="/pass-builder" element={<PassBuilderPage />} />
                                <Route path="/blog" element={<Blog />} />
                                <Route path="/blog/:slug" element={<BlogPost />} />
                                <Route path="/contact" element={<Contact />} />
                                <Route path="/features" element={<FeaturesPage />} />
                                <Route path="/pricing" element={<Pricing />} />
                                <Route path="/privacy" element={<Privacy />} />
                                <Route path="/privacy/:businessId" element={<BusinessPrivacy />} />
                                <Route path="/terms" element={<Terms />} />
                                <Route path="/employee-dashboard" element={<SubscriptionRequiredRoute><EmployeeDashboard /></SubscriptionRequiredRoute>} />
                                <Route path="/employee-portal" element={<EmployeeProtectedRoute><EmployeePortalPage /></EmployeeProtectedRoute>} />
                                <Route path="/portfolio" element={<SubscriptionRequiredRoute><FranchiseDashboard /></SubscriptionRequiredRoute>} />
                                <Route path="/roadmap" element={<Roadmap />} />
                                <Route path="/changelog" element={<Changelog />} />
                                <Route path="/features/*" element={<Navigate to="/features" replace />} />
                                <Route path="/get-pass" element={<EnhancedGetPass />} />
                                <Route path="/update-pass" element={<UpdatePassPage />} />
                                <Route path="/kiosk" element={<KioskPage />} />
                                <Route path="/signage/player" element={<SignagePlayer />} />
                                <Route path="/job-details" element={<JobDetails />} />
                                <Route path="/job-application" element={<JobApplication />} />
                                <Route path="/review/:formId" element={<PublicReviewForm />} />
                                <Route path="/review/:formId/preview" element={<PreviewReviewForm />} />
                                <Route path="/coupon-token/:token" element={<CouponTokenRedemption />} />
                                <Route path="/book/:slug" element={<PublicBookingPage />} />
                                <Route path="/email-unsubscribe/successful" element={<UnsubscribeSuccess />} />
                                <Route path="/email-unsubscribe/already-unsubscribed" element={<UnsubscribeAlready />} />
                                <Route path="/email-unsubscribe/:type" element={<UnsubscribeError />} />
                                <Route path="/settings/account" element={<ProtectedRoute><AccountSettingsPage /></ProtectedRoute>} />
                                <Route path="/settings/business/:businessId" element={<ProtectedRoute><BusinessSettingsPage /></ProtectedRoute>} />
                                {/* Admin routes */}
                                <Route path="/admin" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
                                  <Route index element={<AdminDashboard />} />
                                  <Route path="waitlist" element={<WaitlistManagement />} />
                                  <Route path="users" element={<UsersManagement />} />
                                  <Route path="businesses" element={<BusinessesOverview />} />
                                  <Route path="email-templates" element={<EmailTemplatesManagement />} />
                                </Route>
                                <Route path="*" element={<NotFound />} />
                              </Routes>
                              </Suspense>
                              </GlobalLayout>
                              </CommandPaletteProvider>
                              </KeyboardShortcutsProvider>
                              </NotificationProvider>
                              </OnboardingProvider>
                              </BusinessSettingsProvider>
                              </AccountSettingsProvider>
                              </BusinessProvider>
                              </DashboardTabProvider>
                            </BrowserRouter>
                            </TooltipProvider>
                          </HistoricalEditProvider>
                          </ShowUserProvider>
                    </UnifiedLoadingProvider>
                  </ThemeProvider>
                </PayrollHistoryProvider>
              </SubscriptionProvider>
            </EmployeeSelectionProvider>
          </EmployeeProvider>
          </AppLockProvider>
          </MFAGate>
        </AuthProvider>
      </AuthErrorBoundary>
  </QueryClientProvider>
);

export default App;
